import React, { useState } from "react";
import { Modal, Button, Form, Alert, Spinner } from "react-bootstrap";

function CreateTagDefinitionModal({ show, onHide, onSubmit, loading }) {
  const [formData, setFormData] = useState({
    tag_name: "",
    description: "",
    data_type: "boolean",
    default_value: "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null,
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.tag_name.trim()) {
      newErrors.tag_name = "Tag name is required";
    }

    if (!formData.description.trim()) {
      newErrors.description = "Description is required";
    }

    if (!formData.data_type) {
      newErrors.data_type = "Data type is required";
    }

    if (!formData.default_value) {
      newErrors.default_value = "Default value is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    try {
      await onSubmit(formData);
      setFormData({
        tag_name: "",
        description: "",
        data_type: "boolean",
        default_value: "",
      });
      setErrors({});
    } catch (err) {
      console.error("Error submitting form:", err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      tag_name: "",
      description: "",
      data_type: "boolean",
      default_value: "",
    });
    setErrors({});
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Create New Tag Definition</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>Tag Name</Form.Label>
            <Form.Control
              type="text"
              name="tag_name"
              value={formData.tag_name}
              onChange={handleChange}
              placeholder="e.g., school_admin_access"
              isInvalid={!!errors.tag_name}
              disabled={submitting || loading}
            />
            {errors.tag_name && (
              <Form.Control.Feedback type="invalid">
                {errors.tag_name}
              </Form.Control.Feedback>
            )}
            <Form.Text className="text-muted">
              Use snake_case for consistency
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Describe what this tag controls"
              isInvalid={!!errors.description}
              disabled={submitting || loading}
            />
            {errors.description && (
              <Form.Control.Feedback type="invalid">
                {errors.description}
              </Form.Control.Feedback>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Data Type</Form.Label>
            <Form.Select
              name="data_type"
              value={formData.data_type}
              onChange={handleChange}
              isInvalid={!!errors.data_type}
              disabled={submitting || loading}
            >
              <option value="boolean">Boolean (ON/OFF)</option>
              <option value="number">Number</option>
              <option value="string">String</option>
            </Form.Select>
            {errors.data_type && (
              <Form.Control.Feedback type="invalid">
                {errors.data_type}
              </Form.Control.Feedback>
            )}
            <Form.Text className="text-muted">
              Choose the type of value this tag will store
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Default Value</Form.Label>
            <Form.Control
              type="text"
              name="default_value"
              value={formData.default_value}
              onChange={handleChange}
              placeholder={
                formData.data_type === "boolean"
                  ? "e.g., OFF"
                  : formData.data_type === "number"
                    ? "e.g., 0"
                    : "e.g., default"
              }
              isInvalid={!!errors.default_value}
              disabled={submitting || loading}
            />
            {errors.default_value && (
              <Form.Control.Feedback type="invalid">
                {errors.default_value}
              </Form.Control.Feedback>
            )}
            <Form.Text className="text-muted">
              This value will be used for new instances
            </Form.Text>
          </Form.Group>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={handleClose}
          disabled={submitting || loading}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={submitting || loading}
        >
          {submitting ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Creating...
            </>
          ) : (
            "Create Tag"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default CreateTagDefinitionModal;